import { TRACKS } from './workspaceTracks';

export type ActivityAuthor = 'human' | 'bot';
export type ActivityTag = 'Discussion' | 'Decision' | 'Issue' | 'Progress' | 'Customer signal' | 'Metric';

export interface ActivityEntry {
  time: string;
  text: string;
  tags: ActivityTag[];
  author: ActivityAuthor;
}

export interface ActivityDay {
  date: string;
  entries: ActivityEntry[];
}

export interface LinkedItem {
  title: string;
  meta: string;
  badges?: string[];
}

export interface LinkedCategory {
  id: string;
  name: string;
  items: LinkedItem[];
}

export interface LinkedInventory {
  left: LinkedCategory[];
  right: LinkedCategory[];
}

export interface OverviewChat {
  name: string;
  initials: string;
  when: string;
  ref: string;
}

export interface TrackChatStory {
  threads: string[];
  starters: string[];
  userPrompt: string;
  assistantIntro: string;
  summaryTitle: string;
  highlights: string[];
  workTime: string;
  toolCalls: number;
}

export interface TrackPulse {
  name: string;
  meta: string;
  status: 'Running' | 'Scheduled';
  outcome: string;
}

export interface TrackFile {
  name: string;
  meta: string;
  kind: 'report' | 'spreadsheet' | 'document';
}

const FEATURED_TRACK = 'Inbound leads → qualified pipeline';

const FEATURED_ACTIVITY: ActivityDay[] = [{
  date: 'Today',
  entries: [{
    time: '16:38',
    text: 'Giga enriched 18 new leads and routed 14 to the correct Close owner',
    tags: ['Metric', 'Progress'],
    author: 'bot'
  }, {
    time: '15:52',
    text: 'Maya approved the territory rule for UK and Ireland accounts',
    tags: ['Decision'],
    author: 'human'
  }, {
    time: '14:31',
    text: 'Giga flagged four leads missing a company domain before CRM sync',
    tags: ['Issue'],
    author: 'bot'
  }, {
    time: '11:06',
    text: 'Acme replied to the follow-up and requested a security review',
    tags: ['Customer signal'],
    author: 'bot'
  }, {
    time: '09:12',
    text: 'Monday pipeline brief posted to #revenue-ops with three stalled deals',
    tags: ['Progress'],
    author: 'bot'
  }]
}, {
  date: 'Yesterday',
  entries: [{
    time: '17:24',
    text: 'Sarah changed the high-intent threshold from 70 to 75',
    tags: ['Decision'],
    author: 'human'
  }, {
    time: '15:10',
    text: 'Giga found $184k in pipeline with no activity for more than 14 days',
    tags: ['Metric', 'Issue'],
    author: 'bot'
  }, {
    time: '13:42',
    text: 'The team reviewed attribution gaps from the partner lead form',
    tags: ['Discussion'],
    author: 'human'
  }, {
    time: '10:18',
    text: 'Giga linked the approved CRM field map and routing playbook',
    tags: ['Progress'],
    author: 'bot'
  }]
}];

const FEATURED_LINKED: LinkedInventory = {
  left: [{
    id: 'bookmarks',
    name: 'Bookmarks',
    items: [{
      title: 'Close lead inbox',
      meta: 'Live view · 128 open leads'
    }, {
      title: 'Webflow demo request form',
      meta: 'Primary inbound source'
    }]
  }, {
    id: 'prompts',
    name: 'Saved prompts',
    items: [{
      title: 'Brief me on pipeline risk',
      meta: 'Uses Close activity and call notes'
    }, {
      title: 'Draft the next best follow-up',
      meta: 'Uses deal stage, email and meeting context'
    }]
  }, {
    id: 'skills',
    name: 'Skills',
    items: [{
      title: 'Qualify and route inbound leads',
      meta: 'Enriches, scores and assigns an owner'
    }, {
      title: 'CRM hygiene check',
      meta: 'Finds missing fields and stale opportunities'
    }]
  }, {
    id: 'credentials',
    name: 'Connections',
    items: [{
      title: 'Close CRM',
      meta: 'Read and write · Sarah Chen',
      badges: ['Connected']
    }, {
      title: 'Gmail',
      meta: 'Read threads and draft replies',
      badges: ['Connected']
    }, {
      title: 'Clearbit',
      meta: 'Company and contact enrichment',
      badges: ['Connected']
    }]
  }, {
    id: 'databases',
    name: 'Tables',
    items: [{
      title: 'Target account list',
      meta: '1,248 companies · synced 8 min ago'
    }, {
      title: 'Territory and routing rules',
      meta: 'Owner, region, segment and exceptions'
    }]
  }],
  right: [{
    id: 'slack',
    name: 'Slack channels',
    items: [{
      title: '#revenue-ops',
      meta: 'Briefs, routing exceptions and approvals',
      badges: ['Primary']
    }, {
      title: '#sales',
      meta: 'Qualified lead alerts and deal updates'
    }]
  }, {
    id: 'chats',
    name: 'Linked chats',
    items: [{
      title: 'Why did Acme miss routing?',
      meta: 'Resolved · missing employee count'
    }, {
      title: 'Review stalled enterprise deals',
      meta: '7 opportunities · $184k pipeline'
    }, {
      title: 'Update the UK territory rule',
      meta: 'Approved by Maya yesterday'
    }]
  }, {
    id: 'context',
    name: 'Track context',
    items: [{
      title: 'Revenue operations playbook',
      meta: 'Qualification, ownership and response-time rules'
    }, {
      title: 'CRM field mapping v3',
      meta: 'Approved schema · updated yesterday'
    }, {
      title: 'Q3 pipeline targets',
      meta: 'Targets by segment and region'
    }]
  }]
};

const FEATURED_CHATS: OverviewChat[] = [{
  name: 'Why did Acme miss routing?',
  initials: 'MC',
  when: '12 minutes ago',
  ref: 'a3f19c'
}, {
  name: 'Review stalled enterprise deals',
  initials: 'SC',
  when: 'Today, 10:42',
  ref: 'b72e04'
}, {
  name: 'Update the UK territory rule',
  initials: 'MP',
  when: 'Yesterday',
  ref: 'c18d92'
}, {
  name: 'Monday pipeline briefing',
  initials: 'GG',
  when: 'Monday',
  ref: 'd92a71'
}];

const FEATURED_CHAT_STORY: TrackChatStory = {
  threads: ['Pipeline health check', 'Acme routing exception'],
  starters: [
    'Brief me on today’s pipeline changes',
    'Show leads that still need an owner',
    'Draft follow-ups for stalled deals'
  ],
  userPrompt: 'What changed in the pipeline today, and is anything at risk?',
  assistantIntro: 'I checked Close, Gmail, the territory rules and today’s inbound lead activity. The pipeline is moving, but there are two items worth acting on.',
  summaryTitle: 'Today’s pipeline brief',
  highlights: [
    '18 new leads were enriched; 14 were routed automatically and 4 need a company domain.',
    'Acme requested a security review. I drafted a reply using the latest security documentation.',
    'Three enterprise deals worth $184k have had no activity for 14+ days. Owners are tagged in #revenue-ops.'
  ],
  workTime: '8s',
  toolCalls: 4
};

const FEATURED_PULSES: TrackPulse[] = [{
  name: 'Daily lead routing exceptions',
  meta: 'Weekdays at 8:00 AM · posts to #revenue-ops',
  status: 'Running',
  outcome: '4 leads need a company domain before routing'
}, {
  name: 'Monday pipeline briefing',
  meta: 'Mondays at 8:30 AM · Close + Gmail + Calendar',
  status: 'Scheduled',
  outcome: 'Last run: 3 risks surfaced, 2 follow-ups drafted'
}, {
  name: 'Stalled opportunity watch',
  meta: 'Every 4 hours · alerts assigned deal owners',
  status: 'Scheduled',
  outcome: 'Monitoring $184k across 3 opportunities'
}];

const FEATURED_FILES: TrackFile[] = [{
  name: 'Pipeline brief — Sep 14.pdf',
  meta: 'Generated by Monday pipeline briefing · 9:13 AM',
  kind: 'report'
}, {
  name: 'Lead routing exceptions.csv',
  meta: '4 rows · synced from Close 12 minutes ago',
  kind: 'spreadsheet'
}, {
  name: 'CRM field mapping v3.xlsx',
  meta: 'Approved by Maya · updated yesterday',
  kind: 'spreadsheet'
}, {
  name: 'Revenue operations playbook.docx',
  meta: 'Source context · shared with Marketing',
  kind: 'document'
}];

function seedFor(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 33 + name.charCodeAt(i)) % 99991;
  return hash;
}

const FALLBACK_ACTIVITY: Array<{
  text: (name: string) => string;
  tags: ActivityTag[];
  author: ActivityAuthor;
}> = [{
  text: name => `Giga summarized the latest work and open decisions in ${name}`,
  tags: ['Progress'],
  author: 'bot'
}, {
  text: name => `The team approved the next action for ${name}`,
  tags: ['Decision'],
  author: 'human'
}, {
  text: name => `Giga found one item in ${name} that needs an owner`,
  tags: ['Issue'],
  author: 'bot'
}, {
  text: name => `New customer context was linked to ${name}`,
  tags: ['Customer signal'],
  author: 'bot'
}, {
  text: name => `The team reviewed this week's progress on ${name}`,
  tags: ['Discussion'],
  author: 'human'
}];

export function activityFor(trackName: string): ActivityDay[] {
  if (trackName === FEATURED_TRACK) return FEATURED_ACTIVITY;
  const seed = seedFor(trackName);
  return [{
    date: 'Today',
    entries: FALLBACK_ACTIVITY.map((template, index) => ({
      time: `${16 - index}:${String((seed + index * 11) % 60).padStart(2, '0')}`,
      text: template.text(trackName),
      tags: template.tags,
      author: template.author
    }))
  }];
}

export function activityCountFor(trackName: string): number {
  return trackName === FEATURED_TRACK ? 184 : 140 + seedFor(trackName) % 80;
}

export function attentionCountFor(trackName: string): number {
  return trackName === FEATURED_TRACK ? 4 : 1;
}

export function linkedFor(trackName: string): LinkedInventory {
  if (trackName === FEATURED_TRACK) return FEATURED_LINKED;
  const track = TRACKS.find(item => item.name === trackName);
  const group = track?.group ?? 'Everyone';
  return {
    left: [{
      id: 'skills',
      name: 'Skills',
      items: [{ title: `${trackName} review`, meta: 'Summarizes changes and suggests next actions' }]
    }, {
      id: 'credentials',
      name: 'Connections',
      items: [{ title: 'Google Drive', meta: `Connected for ${group}`, badges: ['Connected'] }]
    }],
    right: [{
      id: 'chats',
      name: 'Linked chats',
      items: [{ title: `Weekly ${trackName} review`, meta: 'Updated today' }]
    }, {
      id: 'context',
      name: 'Track context',
      items: [{ title: `${trackName} working brief`, meta: 'Goals, owners and current decisions' }]
    }]
  };
}

export function linkedCountFor(trackName: string): number {
  const inventory = linkedFor(trackName);
  return [...inventory.left, ...inventory.right].reduce((sum, category) => sum + category.items.length, 0);
}

export function statePlayFor(trackName: string): string {
  if (trackName === FEATURED_TRACK) {
    return 'Inbound demo requests now flow from Webflow into Close with company enrichment, lead scoring and territory-based ownership. Four records need a company domain before they can route, and Giga is watching three stalled enterprise opportunities worth $184k.';
  }
  const track = TRACKS.find(item => item.name === trackName);
  return `${trackName} is active and shared with ${track?.group ?? 'this workspace'}. Giga is keeping the latest conversations, decisions and source material together so the team can pick up the work without rebuilding context.`;
}

export function chatsFor(trackName: string): OverviewChat[] {
  if (trackName === FEATURED_TRACK) return FEATURED_CHATS;
  const seed = seedFor(trackName);
  return ['Weekly review', 'Open questions', 'Next actions'].map((label, index) => ({
    name: `${trackName} · ${label}`,
    initials: index === 0 ? 'GG' : 'SC',
    when: ['Today', 'Yesterday', 'Last week'][index],
    ref: (seed + index * 137).toString(16).slice(0, 6)
  }));
}

export function chatStoryFor(trackName: string): TrackChatStory {
  if (trackName === FEATURED_TRACK) return FEATURED_CHAT_STORY;
  return {
    threads: [`${trackName} weekly review`, 'Open questions'],
    starters: [
      'Give me the latest brief',
      'What needs attention today?',
      'Turn the open items into next steps'
    ],
    userPrompt: `Brief me on ${trackName} and tell me what needs attention.`,
    assistantIntro: `I reviewed the latest linked conversations, files and activity for ${trackName}. Here is the current state and the next work that needs an owner.`,
    summaryTitle: 'Current brief',
    highlights: [
      'The latest source material is linked and summarized.',
      'One open item needs an owner before the next milestone.',
      'The team can continue from this track without searching across tools.'
    ],
    workTime: '6s',
    toolCalls: 3
  };
}

export function pulsesFor(trackName: string): TrackPulse[] {
  if (trackName === FEATURED_TRACK) return FEATURED_PULSES;
  return [{
    name: `Weekly ${trackName} brief`,
    meta: 'Mondays at 8:00 AM · shared with the track group',
    status: 'Scheduled',
    outcome: 'Summarizes changes, risks and next actions'
  }];
}

export function filesFor(trackName: string): TrackFile[] {
  if (trackName === FEATURED_TRACK) return FEATURED_FILES;
  return [{
    name: `${trackName} weekly brief.pdf`,
    meta: 'Generated by Giga · updated this week',
    kind: 'report'
  }, {
    name: `${trackName} working context.docx`,
    meta: 'Linked source · shared with the track group',
    kind: 'document'
  }];
}
